import z from 'zod';
import { t as tGlobal } from 'i18next';
import { useFormHook } from '@Hooks/index';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useBoundStore } from '@Store/useBoundStore';
import { CddVolumeOptions } from './constants';
import { useGetCddDataQuery, useUpdateCddData } from './api/cdd.api';
import type { RoutesMap } from '@Router/router.types';
import type { CddEmbeddedFields } from '../../eddQuestions/pages/direct/field.types';
import type { CddDataFull } from './api/cdd.api.types';
import type { CddVolumesFormValues } from './formatCddDataToFormValues';

const volumeOptionValues = Object.values(CddVolumeOptions) as [
  CddVolumeOptions,
  ...CddVolumeOptions[],
];

const volumeSchema = z
  .string()
  .min(1, { message: tGlobal('enErrors:required') })
  .refine((val): val is CddVolumeOptions => volumeOptionValues.includes(val as CddVolumeOptions), {
    message: tGlobal('enErrors:required'),
  });

export interface UseCddLogicConfig {
  schemaTitle: string;
  route: RoutesMap;
  questionOneFieldName: CddEmbeddedFields;
  questionTwoFieldName: CddEmbeddedFields;
  formatCddDataToFormValues: (data: CddDataFull | undefined) => CddVolumesFormValues;
  getPayload: (values: CddVolumesFormValues) => Partial<CddDataFull>;
}

export const createUseCddLogic = (config: UseCddLogicConfig) => {
  const {
    schemaTitle,
    route,
    questionOneFieldName,
    questionTwoFieldName,
    formatCddDataToFormValues,
    getPayload,
  } = config;

  return () => {
    const { navigateToNextPage } = useNavigationManager();
    const { customerId } = useBoundStore.getState().customer;
    const { data: cddData } = useGetCddDataQuery({ customerId: customerId! });
    const { mutateAsync: postCddData, isPending } = useUpdateCddData({ customerId: customerId! });

    const formMethods = useFormHook({
      schemaTitle,
      formDefaultValues: formatCddDataToFormValues(cddData),
      values: cddData ? formatCddDataToFormValues(cddData) : undefined,
      formSchema: z.object({
        [questionOneFieldName]: volumeSchema,
        [questionTwoFieldName]: volumeSchema,
      }),
    });

    const handleSubmit = async () => {
      await postCddData(getPayload(formMethods.getValues()));
      navigateToNextPage(route);
    };

    return {
      formMethods,
      handleSubmit,
      isLoading: isPending,
      cddData,
    };
  };
};
